import {
  HubSpotConnection,
  IConfirmModalProps,
  SettingCardViewAction,
  UserRoleEnum,
} from '@monetize/types/app';
import { FC, useState } from 'react';
import { useDeleteHubspotCrmConnection } from '../../../../../../api/settingsService';
import { SettingCardEdit } from '../../../../../../components/Settings/SettingCardEdit';
import { SettingCardView } from '../../../../../../components/Settings/SettingCardView';
import { useAuth } from '../../../../../../services/auth0';
import { useToast } from '../../../../../../services/toast';
import { CrmConnectionHealth } from '../../useCrmConnectionHealth';
import { CrmConnectionHealthAlert } from '../CrmConnectionHealthAlert';
import { HubSpotAppSettingsSection } from './HubSpotAppSettingsSection';
import { HubSpotFieldMapping } from './HubSpotFieldMapping';

interface HubspotCrmConnectionProps {
  connection: HubSpotConnection;
  health: CrmConnectionHealth;
  reAuthorize: () => void;
  verifyConnection: () => Promise<void>;
}

const DELETE_CONFIRM_PROPS: Partial<IConfirmModalProps> = {
  title: 'Delete HubSpot connection',
  description:
    'Removing this connection will disconnect HubSpot from MonetizeNow. None of your data in HubSpot will be modified.',
  yesButton: 'Delete',
  noButton: 'Cancel',
};

export const HubspotCrmConnection: FC<HubspotCrmConnectionProps> = ({
  connection,
  health,
  reAuthorize,
  verifyConnection,
}) => {
  const [mode, setMode] = useState<'VIEW' | 'EDIT'>('VIEW');
  const { currentTenant, currentTenantUserHasSpecificRole } = useAuth();
  const { addToast } = useToast();

  const canDeleteConnection =
    !!currentTenant?.testTenant ||
    currentTenantUserHasSpecificRole(UserRoleEnum.TENANT_ADMIN);

  const { mutateAsync: deleteConnection } = useDeleteHubspotCrmConnection({
    onSuccess: () => {
      addToast({
        summary: 'Connection Deleted',
        detail: 'Your HubSpot connection has been deleted.',
        severity: 'success',
      });
    },
  });

  const handleAction = async (action: SettingCardViewAction) => {
    switch (action) {
      case 'RE_AUTHENTICATE':
        reAuthorize();
        break;
      case 'VERIFY':
        await verifyConnection();
        break;
      case 'DELETE':
        await deleteConnection();
        break;
      default:
        break;
    }
  };

  const actions: {
    action: SettingCardViewAction;
    label: string;
    isDanger?: boolean;
    confirmProps?: Partial<IConfirmModalProps> | null;
  }[] = [
    { action: 'RE_AUTHENTICATE', label: 'Re-Authenticate' },
    { action: 'VERIFY', label: 'Verify Connection' },
  ];
  if (canDeleteConnection) {
    actions.push({
      action: 'DELETE',
      label: 'Delete',
      isDanger: true,
      confirmProps: DELETE_CONFIRM_PROPS,
    });
  }

  return (
    <>
      <CrmConnectionHealthAlert
        crmLabel="HubSpot"
        health={health}
        onReAuthenticate={reAuthorize}
      />
      {mode === 'VIEW' && (
        <SettingCardView
          omitEditButton
          status={health.status === 'unhealthy' ? 'ERROR' : 'ACTIVE'}
          actions={actions}
          type="HubSpot"
          tags={[
            {
              label: 'Hub ID',
              value: connection.hubId,
            },
            ...(connection.userEmail
              ? [{ label: 'Username', value: connection.userEmail }]
              : []),
            {
              label: 'Hub Domain',
              value: connection.hubDomain,
            },
          ]}
          onEdit={() => setMode('EDIT')}
          onAction={handleAction}
        />
      )}
      {mode === 'EDIT' && (
        <SettingCardEdit
          type="HubSpot"
          showCloseButton
          handleCancel={() => setMode('VIEW')}
        >
          TODO
        </SettingCardEdit>
      )}

      <HubSpotFieldMapping
        existingDealAmountMap={connection.dealAmountMapping}
      />
      <HubSpotAppSettingsSection syncBehavior={connection.syncBehavior} />
    </>
  );
};
